import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ProjectApi from "../../api/projectApi";
import TestCaseApi from "../../api/TestCaseApi";

function TestCases() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =========================================
     FETCH TEST CASE PROJECTS
  ========================================= */

  const fetchTestCaseProjects = async () => {
    try {
      setLoading(true);

      const projectRes = await ProjectApi.assignedTestingProjects();

      console.log("TEST CASE PROJECT RESPONSE:", projectRes);

      const responseCode =
        projectRes?.data?.code ??
        projectRes?.code ??
        projectRes?.status;

      if (responseCode !== 200) {
        setProjects([]);

        toast.error(
          projectRes?.data?.message ||
            projectRes?.message ||
            "Unable to fetch testing projects",
        );

        return;
      }

      const projectData =
        projectRes?.data?.data?.projects ||
        projectRes?.data?.projects ||
        projectRes?.data?.data ||
        projectRes?.data ||
        [];

      if (!Array.isArray(projectData)) {
        setProjects([]);

        toast.error("Invalid project data received");

        return;
      }

      const projectsWithStats = await Promise.all(
        projectData.map(async (project) => {
          try {
            const testCaseRes =
              await TestCaseApi.getProjectTestCases(project._id);

            console.log(
              "TEST CASES:",
              project._id,
              testCaseRes,
            );

            const testCases =
              testCaseRes?.data?.data ||
              testCaseRes?.data?.testCases ||
              testCaseRes?.data ||
              [];

            const cases = Array.isArray(testCases)
              ? testCases
              : [];

            const total = cases.length;

            const passed = cases.filter(
              (item) => item?.status === "passed",
            ).length;

            const failed = cases.filter(
              (item) => item?.status === "failed",
            ).length;

            const pending = cases.filter(
              (item) => item?.status === "pending",
            ).length;

            const blocked = cases.filter(
              (item) => item?.status === "blocked",
            ).length;

            const completed =
              passed + failed + blocked;

            const progress =
              total > 0
                ? Math.round(
                    (completed / total) * 100,
                  )
                : 0;

            return {
              ...project,

              testCaseStats: {
                total,
                passed,
                failed,
                pending,
                blocked,
                progress,
              },
            };
          } catch (error) {
            console.error(
              "TEST CASE STATS ERROR:",
              project?._id,
              error,
            );

            return {
              ...project,

              testCaseStats: {
                total: 0,
                passed: 0,
                failed: 0,
                pending: 0,
                blocked: 0,
                progress: 0,
              },
            };
          }
        }),
      );

      setProjects(projectsWithStats);
    } catch (error) {
      console.error(
        "FETCH TEST CASE PROJECT ERROR:",
        error,
      );

      setProjects([]);

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch testing projects",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {
    fetchTestCaseProjects();
  }, []);

  /* =========================================
     LOADING
  ========================================= */

  if (loading) {
    return (
      <div className="content">
        <div className="text-center py-5">
          Loading Test Cases...
        </div>
      </div>
    );
  }

  /* =========================================
     UI
  ========================================= */

  return (
    <div className="content">

      {/* =====================================
          HEADER
      ===================================== */}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">
            📋 Test Cases
          </h3>

          <p className="text-muted mb-0">
            Create, manage and execute test cases
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={fetchTestCaseProjects}
        >
          ↻ Refresh
        </button>
      </div>

      {/* =====================================
          EMPTY STATE
      ===================================== */}

      {projects.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">

            <div
              style={{
                fontSize: "55px",
                marginBottom: "15px",
              }}
            >
              📋
            </div>

            <h5 className="fw-bold">
              No Testing Projects Available
            </h5>

            <p className="text-muted mb-0">
              No projects are currently available
              for test case management.
            </p>

          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">

          <div className="card-body p-0">

            <div className="table-responsive">

              <table className="table table-hover align-middle mb-0">

                <thead className="table-light">

                  <tr>
                    <th className="ps-4">
                      Project
                    </th>

                    <th>
                      Total Cases
                    </th>

                    <th>
                      Passed
                    </th>

                    <th>
                      Failed
                    </th>

                    <th>
                      Pending
                    </th>

                    <th>
                      Progress
                    </th>

                    <th className="text-end pe-4">
                      Action
                    </th>
                  </tr>

                </thead>

                <tbody>

                  {projects.map((project) => {

                    const stats =
                      project?.testCaseStats || {};

                    return (
                      <tr key={project?._id}>

                        {/* PROJECT */}

                        <td className="ps-4">

                          <div className="fw-bold">
                            {project?.projectName || "-"}
                          </div>

                          <small className="text-muted">
                            Developer:{" "}
                            {project?.assignedDeveloper
                              ?.name || "-"}
                          </small>

                        </td>

                        {/* TOTAL */}

                        <td>
                          <span className="fw-bold">
                            {stats?.total || 0}
                          </span>
                        </td>

                        {/* PASSED */}

                        <td>
                          <span className="badge bg-success px-3 py-2">
                            ✓ {stats?.passed || 0}
                          </span>
                        </td>

                        {/* FAILED */}

                        <td>
                          <span className="badge bg-danger px-3 py-2">
                            ✕ {stats?.failed || 0}
                          </span>
                        </td>

                        {/* PENDING */}

                        <td>
                          <span className="badge bg-warning text-dark px-3 py-2">
                            {stats?.pending || 0}
                          </span>
                        </td>

                        {/* PROGRESS */}

                        <td style={{ minWidth: "170px" }}>

                          <div className="d-flex align-items-center gap-2">

                            <div
                              className="progress flex-grow-1"
                              style={{
                                height: "8px",
                              }}
                            >

                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  width: `${stats?.progress || 0}%`,
                                }}
                              />

                            </div>

                            <small className="fw-bold">
                              {stats?.progress || 0}%
                            </small>

                          </div>

                        </td>

                        {/* ACTION */}

                        <td className="text-end pe-4">

                          <button
                            type="button"
                            className="btn btn-primary btn-sm px-3"
                            onClick={() => {
                              if (!project?._id) {
                                toast.error(
                                  "Project ID not found",
                                );
                                return;
                              }

                              navigate(
                                `/test-cases/${project._id}`,
                              );
                            }}
                          >
                            Manage Cases
                          </button>

                        </td>

                      </tr>
                    );
                  })}

                </tbody>

              </table>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}

export default TestCases;